import { CEFR_LEVELS, LEVEL_COMPLETION_THRESHOLD_PCT, type CefrLevel } from "@rmnlng/shared";
import { ArrowRight, BookOpen, ChevronRight, Clock, GraduationCap, Info, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { HomeSkillsBody } from "@/components/home/HomeSkillsBody";
import { ModeSwitch3 } from "@/components/ModeSwitch3";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useMe } from "@/lib/auth";
import { features } from "@/lib/features";
import { useGrammarList } from "@/lib/grammar";
import { formatDate, useLang, useT } from "@/lib/i18n";
import { formatStudyDuration, useProgressLevels, useProgressStats, type LevelProgress } from "@/lib/progress";
import { useReviewQueue } from "@/lib/reviews";

/**
 * Pick the user's display name for the home greeting.
 *
 * Order of preference:
 *   1. `user.name` — captured at signup or set in Settings.
 *   2. Email prefix, capitalized — fallback for legacy/imported
 *      accounts that pre-date the name field, or signups where the
 *      user left the name blank.
 *
 * Returns `""` when both are missing — the greeting then renders the
 * generic `home.greeting` ("Привет") instead of `home.greeting_with_name`.
 */
function displayName(name: string | null | undefined, email: string | undefined): string {
  const trimmed = name?.trim();
  if (trimmed && trimmed.length > 0) return trimmed;
  if (!email) return "";
  const prefix = email.split("@")[0] ?? "";
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

export function HomePage() {
  const { data: user } = useMe();
  const t = useT();
  const lang = useLang();

  const studyMode = user?.studyMode ?? "LEVEL";
  const name = displayName(user?.name, user?.email);

  return (
    <div className="flex flex-col gap-[14px]">
      {/* ─── Header ─────────────────────────────────────────────
          Date + greeting on the left, ModeSwitch3 on the right.
          Settings moved into the bottom nav (rightmost slot), so the
          header is just identity + mode. Streak shown as a compact
          inline flame next to the greeting. Mode switching is
          front-and-center per `design/hifi/scenarios.jsx
          HomeScenariosScreen` — the user can hop between
          Open / Level / Skills without going through Settings, and
          the active segment doubles as a status badge (Open / A1 /
          14 mastered). */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-text-3">
            {formatDate(new Date(), lang, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </div>
          <h1 className="mt-0.5 flex items-center gap-2 text-[22px] font-bold tracking-[-0.02em]">
            <span className="truncate">{name ? t("home.greeting_with_name", { name }) : t("home.greeting")}</span>
          </h1>
        </div>
        <ModeSwitch3 />
      </div>

      {/* ─── Body — dispatched on study mode ────────────────────
          Each body fetches its own scoped queue (server applies the
          level filter via `useReviewQueue` reading user.studyMode +
          currentLevel). Switching modes invalidates the cache key so
          the new body fetches with the right scope. */}
      {features.skillsEnabled && studyMode === "SCENARIOS" ? (
        <HomeSkillsBody />
      ) : studyMode === "LEVEL" ? (
        <HomeLevelBody />
      ) : (
        <HomeOpenBody />
      )}
    </div>
  );
}

// ─── Home (OPEN mode body) ────────────────────────────────────
// Current behavior preserved verbatim — Due hero + Stats strip + Grammar
// preview + Vocabulary link. Adds a small "Open mode" hint card at the
// bottom so the user knows the mode is active and can switch to Level.
function HomeOpenBody() {
  const { data: stats, isPending: statsPending } = useProgressStats();
  // Fetch ALL kinds (words + grammar + quizzes) so the hero can show
  // per-flavor counts for the two session CTAs. When the grammar
  // section is feature-disabled we keep the legacy words-only fetch.
  const { data: queue, isPending: queuePending } = useReviewQueue({ includeGrammar: features.grammarSection });
  const { data: grammar, isPending: grammarPending } = useGrammarList();
  // First page is plenty for a 2-row preview — no need to paginate.
  const grammarPreview = (grammar?.pages[0]?.items ?? []).slice(0, 2);
  const t = useT();

  const dueCount = queue?.length ?? 0;
  const newCount = queue?.filter((c) => c.isNew).length ?? 0;
  const reviewCount = dueCount - newCount;
  const minutes = Math.max(1, Math.round(dueCount * 0.3));
  // Per-flavor counts for the split CTAs. Quiz cards belong to the
  // grammar session — they're rule practice, not vocabulary.
  const wordCount = queue?.filter((c) => c.kind === "WORD").length ?? 0;
  const grammarCount = dueCount - wordCount;

  return (
    <>
      {/* Due today hero */}
      {queuePending ? (
        <DueHeroSkeleton />
      ) : (
        <div
          className="rounded-[var(--r-lg)] border-0 p-5 shadow-[var(--shadow-md)]"
          style={{
            background: "linear-gradient(180deg, var(--surface) 0%, var(--surface) 60%, var(--accent-soft) 140%)",
          }}
        >
          <div className="mb-3 flex items-center justify-between">
            <Badge variant="accent">{t("home.due_today")}</Badge>
            <span className="flex items-center gap-1 text-[12px] text-text-3">
              <Clock className="size-3 opacity-70" />
              {t("home.about_minutes", { n: minutes })}
            </span>
          </div>
          <div className="mb-1 flex items-baseline gap-2">
            <span className="text-[48px] font-bold leading-none tracking-[-0.04em]">{dueCount}</span>
            <span className="text-[15px] text-text-3">{t("home.cards")}</span>
          </div>
          <div className="mb-4 text-[13px] text-text-2">
            {newCount > 0 && (
              <>
                <span className="font-semibold text-[color:var(--accent)]">{t("home.new_count", { n: newCount })}</span>
                {reviewCount > 0 && " · "}
              </>
            )}
            {reviewCount > 0 && t("home.reviews_count", { n: reviewCount })}
            {dueCount === 0 && t("home.all_caught_up")}
          </div>
          <div className="progress-track mb-[14px]">
            <div className="fill" style={{ width: "0%" }} />
          </div>
          {/* Split session CTAs — words and grammar are separate study
              flavors since the mixed mode was retired. Grammar hides
              entirely behind its feature flag. */}
          {features.grammarSection ? (
            <div className="grid grid-cols-2 gap-2">
              <Button asChild disabled={wordCount === 0}>
                <Link to="/study?mode=words">
                  <Play className="size-4" />
                  <span className="truncate">{t("home.study_words", { n: wordCount })}</span>
                </Link>
              </Button>
              <Button asChild variant="secondary" disabled={grammarCount === 0}>
                <Link to="/study?mode=grammar">
                  <GraduationCap className="size-4" />
                  <span className="truncate">{t("home.study_grammar", { n: grammarCount })}</span>
                </Link>
              </Button>
            </div>
          ) : (
            <Button asChild size="block" disabled={dueCount === 0}>
              <Link to="/study?mode=words">
                <Play className="size-4" />
                {t("home.start_session")}
              </Link>
            </Button>
          )}
        </div>
      )}

      {/* Stats strip */}
      {statsPending ? (
        <div className="grid grid-cols-3 gap-2.5">
          <StatTileSkeleton />
          <StatTileSkeleton />
          <StatTileSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2.5">
          <div className="stat-tile">
            <div className="n accent">{stats?.wordsMature ?? 0}</div>
            <div className="lbl">{t("home.stat_known")}</div>
          </div>
          <div className="stat-tile">
            <div className="n">{stats?.wordsLearning ?? 0}</div>
            <div className="lbl">{t("home.stat_learning")}</div>
          </div>
          <div className="stat-tile">
            <div className="n">{formatStudyDuration(stats?.totalStudySeconds ?? 0)}</div>
            <div className="lbl">{t("home.stat_studied")}</div>
          </div>
        </div>
      )}

      {/* Grammar quick list — only meaningful in Open mode (Level mode's
          "Today" card already covers grammar). Gated on the feature flag
          so a disabled grammar section doesn't render a skeleton that
          never resolves. */}
      {grammarPending && features.grammarSection ? (
        <GrammarPreviewSkeleton />
      ) : (
        grammarPreview.length > 0 && (
          <section>
            <div className="mb-2 flex items-baseline justify-between px-0.5 pt-1">
              <div className="text-[13px] font-semibold uppercase tracking-[0.06em] text-text-3">
                {t("home.grammar_title")}
              </div>
              <Link to="/grammar" className="text-[13px] font-medium text-[color:var(--accent)]">
                {t("home.see_all")}
              </Link>
            </div>
            <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
              {grammarPreview.map((r, i) => (
                <Link
                  key={r.id}
                  to={`/grammar/${r.id}`}
                  className={
                    "flex items-center justify-between px-4 py-3.5 transition-colors hover:bg-[color:var(--surface-2)]" +
                    (i > 0 ? " border-t border-border" : "")
                  }
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold">{r.title}</div>
                    {r.tags.length > 0 && (
                      <div className="mt-0.5 truncate text-[12px] text-text-3">
                        {r.tags.map((tag) => `#${tag}`).join(" ")}
                      </div>
                    )}
                  </div>
                  {r.state === "known" ? (
                    <Badge variant="default">{t("home.grammar_known")}</Badge>
                  ) : r.state === "learning" ? (
                    <Badge variant="accent">{t("home.grammar_review")}</Badge>
                  ) : (
                    <Badge variant="default">{t("home.grammar_new")}</Badge>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )
      )}

      {/* Vocabulary link */}
      <section>
        <Link
          to="/words"
          className="flex items-center gap-3 rounded-[var(--r-lg)] border border-border bg-surface px-4 py-3.5 transition-colors hover:bg-[color:var(--surface-2)]"
        >
          <div
            className="flex size-9 shrink-0 items-center justify-center rounded-lg"
            style={{
              background: "var(--accent-soft)",
              color: "var(--accent)",
            }}
          >
            <BookOpen className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold">{t("home.vocabulary")}</div>
            {statsPending ? (
              <Skeleton className="mt-1 h-3 w-32" />
            ) : (
              <div className="mt-0.5 text-[12px] text-text-3">
                {t("home.browse_words", { n: stats?.wordsTotalRo ?? 0 })}
              </div>
            )}
          </div>
          <ChevronRight className="size-4 shrink-0 text-text-3" />
        </Link>
      </section>

      {/* Mode hint — explains the mode they're in + how to switch. Subtle
          (surface-2 background, small text) so it doesn't compete with
          the primary CTAs above. */}
      <ModeHint kind="OPEN" />
    </>
  );
}

// ─── Home (LEVEL mode body) ───────────────────────────────────
// Renders `design/hifi/home.jsx → HomeLevelScreen`: level hero with dual
// progress bars + a "Today in {level}" breakdown + a mode hint at the
// bottom. Drops the all-vocabulary tile and grammar preview that
// HomeOpenBody shows — Level mode is intentionally focused.
function HomeLevelBody() {
  const t = useT();
  const { data: levels, isPending: levelsPending } = useProgressLevels();
  // Server auto-includes grammar in LEVEL mode, so no opt-in needed.
  // Hook reads studyMode + currentLevel from useMe internally.
  const { data: queue, isPending: queuePending } = useReviewQueue();
  const { data: stats, isPending: statsPending } = useProgressStats();
  // Pull the current level's row out of the response. `useProgressLevels`
  // always returns 7 levels in canonical order, so .find is safe.
  const current = levels?.levels.find((l) => l.level === levels.currentLevel);

  // Per-card-type breakdown of today's queue. In Phase 3 the queue is
  // still global (level filtering arrives in Phase 4) — so until then
  // these counts may include cards from other levels. The label says
  // "Today in {level}" anyway because that's where it'll point once
  // Phase 4 lands; flipping the data behind it is a one-line change.
  const newVocab = queue?.filter((c) => c.kind === "WORD" && c.isNew).length ?? 0;
  const reviews = queue?.filter((c) => c.kind === "WORD" && !c.isNew).length ?? 0;
  // Quiz cards count as grammar work — they're rule practice.
  const grammar = queue?.filter((c) => c.kind !== "WORD").length ?? 0;
  const totalToday = newVocab + reviews + grammar;
  const minutes = Math.max(1, Math.round(totalToday * 0.3));

  return (
    <>
      {levelsPending || !levels || !current ? (
        <LevelHeroSkeleton />
      ) : (
        <LevelHero current={current} totalToday={totalToday} wordCount={newVocab + reviews} grammarCount={grammar} />
      )}

      {/* Today in {level} breakdown */}
      {queuePending || !levels ? (
        <TodayCardSkeleton />
      ) : (
        <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
          <div className="mb-2.5 flex items-center justify-between">
            <div className="text-[13px] font-semibold">{t("home.today_in_level", { level: levels.currentLevel })}</div>
            <span className="font-mono text-[11px] text-text-3">{t("home.about_minutes", { n: minutes })}</span>
          </div>
          <div className="flex flex-col gap-2">
            <TodayRow color="var(--accent)" label={t("home.row_new_vocab")} value={newVocab} />
            <TodayRow color="var(--text-2)" label={t("home.row_reviews")} value={reviews} />
            <TodayRow color="oklch(58% 0.12 150)" label={t("home.row_grammar")} value={grammar} />
          </div>
        </div>
      )}
      {/* Vocabulary link */}
      <section>
        <Link
          to="/words"
          className="flex items-center gap-3 rounded-[var(--r-lg)] border border-border bg-surface px-4 py-3.5 transition-colors hover:bg-[color:var(--surface-2)]"
        >
          <div
            className="flex size-9 shrink-0 items-center justify-center rounded-lg"
            style={{
              background: "var(--accent-soft)",
              color: "var(--accent)",
            }}
          >
            <BookOpen className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold">{t("home.vocabulary")}</div>
            {statsPending ? (
              <Skeleton className="mt-1 h-3 w-32" />
            ) : (
              <div className="mt-0.5 text-[12px] text-text-3">
                {t("home.browse_words", { n: stats?.wordsTotalRo ?? 0 })}
              </div>
            )}
          </div>
          <ChevronRight className="size-4 shrink-0 text-text-3" />
        </Link>
      </section>

      <ModeHint kind="LEVEL" level={levels?.currentLevel} />
    </>
  );
}

// Level hero card — A1 badge + dual progress bars + "Continue" CTA.
// Extracted because the layout is dense and the calculation for "X cards
// to level up" is non-obvious. The math: each side needs `ceil(85% * total)`
// known cards; sum the deficits.
function LevelHero({
  current,
  totalToday,
  wordCount,
  grammarCount,
}: {
  current: LevelProgress;
  totalToday: number;
  wordCount: number;
  grammarCount: number;
}) {
  const t = useT();
  const vocabDeficit = Math.max(
    0,
    Math.ceil((LEVEL_COMPLETION_THRESHOLD_PCT / 100) * current.vocab.total) - current.vocab.known,
  );
  const grammarDeficit = Math.max(
    0,
    Math.ceil((LEVEL_COMPLETION_THRESHOLD_PCT / 100) * current.grammar.total) - current.grammar.known,
  );
  const cardsToLevelUp = vocabDeficit + grammarDeficit;

  // When `readyForExam`, the CTA flips from "Continue {level}" to
  // "Final exam · {nextLevel}" and routes to the exam lobby instead
  // of the study queue. nextLevel is the level immediately above
  // current; null only for C2, where the CTA stays on Continue.
  const currentIdx = CEFR_LEVELS.indexOf(current.level);
  const nextLevel: CefrLevel | null =
    currentIdx >= 0 && currentIdx < CEFR_LEVELS.length - 1 ? CEFR_LEVELS[currentIdx + 1]! : null;
  const showExamCta = current.readyForExam && nextLevel !== null;

  return (
    <div
      className="rounded-[var(--r-lg)] border-0 p-[18px] shadow-[var(--shadow-md)]"
      style={{
        background: "linear-gradient(140deg, var(--accent-soft), var(--surface) 70%)",
      }}
    >
      <div className="mb-3.5 flex items-center gap-3">
        <div
          className="flex size-11 shrink-0 items-center justify-center rounded-[10px] font-mono text-[16px] font-bold"
          style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
        >
          {current.level}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold">{t(`level.name_${current.level}`)}</div>
          <div className="mt-px text-[12px] text-text-3">
            {current.readyForExam ? t("home.ready_to_advance") : t("home.cards_to_level_up", { n: cardsToLevelUp })}
          </div>
        </div>
        <Link
          to="/levels"
          className="inline-flex items-center gap-0.5 text-[13px] font-medium text-[color:var(--accent)]"
        >
          {t("home.path")}
          <ArrowRight className="size-3.5" />
        </Link>
      </div>

      {/* Dual progress bars — vocab in accent, grammar in green. The
          green hex (oklch 58% 0.12 150) matches the design's
          grammar-vs-vocab visual code used everywhere else (Levels page,
          Progress page by-level strip). */}
      <div className="mb-3.5 flex gap-3">
        <ProgressBar
          label={t("home.bar_vocab")}
          known={current.vocab.known}
          total={current.vocab.total}
          pct={current.vocab.pct}
          color="var(--accent)"
        />
        <ProgressBar
          label={t("home.bar_grammar")}
          known={current.grammar.known}
          total={current.grammar.total}
          pct={current.grammar.pct}
          color="oklch(58% 0.12 150)"
        />
      </div>

      {/* Exam-ready keeps the single full-width CTA; otherwise the two
          session flavors get their own buttons (mixed mode is retired). */}
      {showExamCta ? (
        <Button asChild size="block">
          <Link to={`/levels/${nextLevel}/exam`}>
            <Play className="size-4" />
            {t("home.cta_final_exam", { level: nextLevel })}
          </Link>
        </Button>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button asChild disabled={wordCount === 0 && totalToday === 0}>
            <Link to="/study?mode=words">
              <Play className="size-4" />
              <span className="truncate">{t("home.study_words", { n: wordCount })}</span>
            </Link>
          </Button>
          <Button asChild variant="secondary" disabled={grammarCount === 0}>
            <Link to="/study?mode=grammar">
              <GraduationCap className="size-4" />
              <span className="truncate">{t("home.study_grammar", { n: grammarCount })}</span>
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function ProgressBar({
  label,
  known,
  total,
  pct,
  color,
}: {
  label: string;
  known: number;
  total: number;
  pct: number;
  color: string;
}) {
  return (
    <div className="flex-1">
      <div className="mb-1 flex justify-between font-mono text-[10px] text-text-3">
        <span>{label}</span>
        <span>
          {known}/{total} · {pct}%
        </span>
      </div>
      <div className="progress" style={{ height: 5 }}>
        <div className="fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function TodayRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="size-1.5 rounded-full" style={{ background: color }} />
      <span className="flex-1 text-[13px] text-text-2">{label}</span>
      <span className="font-mono text-[13px] font-semibold text-text">{value}</span>
    </div>
  );
}

// Mode hint footer — subtle cue about the active mode + the affordance to
// flip it. Since ModeSwitch moved out of the header, this card is now the
// only place a user discovers they can change modes; the trailing
// "Settings" link routes them there. Surface-2 bg keeps it out of the
// visual hierarchy of the primary CTAs above.
function ModeHint({ kind, level }: { kind: "OPEN" | "LEVEL"; level?: string }) {
  const t = useT();
  return (
    <div className="flex items-start gap-2.5 rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-3.5 py-3 text-[12px] text-text-2">
      <Info className="mt-0.5 size-4 shrink-0 text-text-3" />
      <div className="flex-1">
        {kind === "OPEN" ? (
          <>
            <b className="text-text">{t("home.mode_hint_open_title")}</b> — {t("home.mode_hint_open_body")}
          </>
        ) : (
          <>
            <b className="text-text">{t("home.mode_hint_level_title")}</b> —{" "}
            {t("home.mode_hint_level_body", { level: level ?? "" })}
          </>
        )}{" "}
        <Link to="/settings" className="font-medium text-[color:var(--accent)] underline-offset-2 hover:underline">
          {t("home.mode_hint_switch_link")}
        </Link>
      </div>
    </div>
  );
}

// ─── Skeleton sub-components ──────────────────────────────────
// Sized to match each block's actual layout so reflow on data-arrival
// is zero.

function DueHeroSkeleton() {
  return (
    <div className="rounded-[var(--r-lg)] border-0 bg-surface p-5 shadow-[var(--shadow-md)]">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-5 w-20 rounded-full" />
        <Skeleton className="h-3 w-14" />
      </div>
      <Skeleton className="mb-2 h-12 w-24" />
      <Skeleton className="mb-4 h-3 w-36" />
      <Skeleton className="mb-[14px] h-1.5 w-full rounded-full" />
      <Skeleton className="h-11 w-full rounded-[var(--r-md)]" />
    </div>
  );
}

function StatTileSkeleton() {
  return (
    <div className="stat-tile">
      <Skeleton className="mb-1.5 h-8 w-10" />
      <Skeleton className="h-3 w-14" />
    </div>
  );
}

function GrammarPreviewSkeleton() {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between px-0.5 pt-1">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-12" />
      </div>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
        {[0, 1].map((i) => (
          <div
            key={i}
            className={"flex items-center justify-between px-4 py-3.5" + (i > 0 ? " border-t border-border" : "")}
          >
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-1.5 h-3 w-24" />
            </div>
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        ))}
      </div>
    </section>
  );
}

function LevelHeroSkeleton() {
  return (
    <div className="rounded-[var(--r-lg)] border-0 bg-surface p-[18px] shadow-[var(--shadow-md)]">
      <div className="mb-3.5 flex items-center gap-3">
        <Skeleton className="size-11 rounded-[10px]" />
        <div className="min-w-0 flex-1">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-1 h-3 w-32" />
        </div>
      </div>
      <div className="mb-3.5 flex gap-3">
        <Skeleton className="h-5 flex-1 rounded-full" />
        <Skeleton className="h-5 flex-1 rounded-full" />
      </div>
      <Skeleton className="h-11 w-full rounded-[var(--r-md)]" />
    </div>
  );
}

function TodayCardSkeleton() {
  return (
    <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
      <div className="mb-2.5 flex items-center justify-between">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-14" />
      </div>
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-2.5">
            <Skeleton className="size-1.5 rounded-full" />
            <Skeleton className="h-3 flex-1" />
            <Skeleton className="h-3 w-6" />
          </div>
        ))}
      </div>
    </div>
  );
}
